import { useState, useEffect, useRef } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { useCart, type CartItem } from "@/utils/cartContext";
import { useToast } from "@/hooks/use-toast";
import { ADD_CAMPAIGN_CART, GET_PACKAGE_DETAILS } from "@/Graphql/packageQurie";
import { DELETE_CART_SUBMIT } from "@/Graphql/cartQueries";
import { ArrowLeft, Eye, Monitor, ShoppingCart, MapPin, ChevronLeft, ChevronRight, Zap, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
// Import mock data for development
import mockPackageDetails from "@/data/mock-package-details.json";
import client from "@/Graphql/apolloClient";
import { CART_PAGE_ITEM } from "@/Graphql/Queries";
import { useAuth } from "@/utils/authContext";
import { format } from 'date-fns';
import logger from '@/utils/logger';

interface Inventory {
  packageId: string;
  inventoryId: string;
  name: string;
  referenceId: string;
  type: string;
  thumbnail: string;
  location: string;
  screens: number;
  size: string;
  venueType: string;
  group: string;
  latitude: number;
  longitude: number;
  price: number;
  sellingRate: number;
  spotDuration: number;
  displayOnTime: string;
  displayOffTime: string;
  clients: number;
  estimation: {
    potentialViews: number;
    uniqueReach: number;
    avgFrequency: number;
    cpm: number;
    spotsPerHour: number;
  };
  specification: any | null;
}

interface PackageBillboard {
  id: string;
  name: string;
  description: string;
  country: {
    id: string;
    name: string;
    timezone: string;
  };
  publisher: {
    id: string;
    name: string;
  };
  startDate: string;
  endDate: string;
  validityStartDate: string;
  validityEndDate: string;
  registryPackageType: string;
  campaignDuration: number;
  purchaseLimitation: number;
  numberOfPurchase: number;
  inventory: {
    type: string[];
    total: number;
    classic: number;
    digital: number;
  };
  exposure: string[];
  currency: string;
  price: number;
  discount: number;
  totalPrice: number;
  thumbnail: string;
  estimation: {
    potentialViews: number;
    uniqueReach: number;
    avgFrequency: number;
    cpm: number;
    spotsPerHour: number;
    totalSpots: number;
  };
  inventories: Inventory[];
  publishTo: string[];
  tagIds: string[] | null;
  tags: string[];
  reopened: boolean;
  createdBy: string;
  createdDate: string;
  lastModifiedBy: string;
  lastModifiedDate: string;
}

interface PackageDetails {
  base_path: string;
  currencyCode: string;
}

export default function PackageDetail({ base_path, currencyCode }: PackageDetails) {
  const { toast } = useToast();
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const { id: packageId } = useParams();
  const urlParams = new URLSearchParams(window.location.search);
  const location = useLocation();
  const currencyCodeFromState = location.state?.currencyCode || location.state?.currencyCOde || 'N/A';
  const campaignStartDate = location.state?.startDate ? new Date(location.state.startDate) : null;
  const campaignEndDate = location.state?.endDate ? new Date(location.state.endDate) : null;

  logger.log('[PackageDetail] location.state:', location.state);
  logger.log('[PackageDetail] campaignStartDate:', campaignStartDate, 'campaignEndDate:', campaignEndDate);
  const [edit, setEdit] = useState<boolean>(() => {

    if (urlParams.get('edit') === 'true') {
      return true;
    }
    return false;
  });
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const { isInCart, addToCart: addToCartContext, removeFromCart: removeFromCartContext } = useCart();
  const [addCampaignCart, { loading: addCartLoading }] = useMutation(ADD_CAMPAIGN_CART);
  const [deleteCartQuery] = useMutation(DELETE_CART_SUBMIT);
  const [loadingCartItemIds, setLoadingCartItemIds] = useState<Set<string>>(new Set());
  const [cartCount, setCartCount] = useState(0);
  const [cartDataList, setCartDataList] = useState<any[]>([]);
  const [isCartLoading, setIsCartLoading] = useState(false);
  const cartRequestInProgress = useRef(false);

  // Fetch cart details on component mount
  useEffect(() => {
    if (localStorage.getItem('authToken')) {
      fetchCartDetails();
    }
  }, []);

  const fetchCartDetails = async () => {
    if (cartRequestInProgress.current) {
      return;
    }

    cartRequestInProgress.current = true;
    setIsCartLoading(true);

    try {
      const authToken = localStorage.getItem('authToken');
      const userId = localStorage.getItem('userId');

      if (!authToken || !userId) {
        return;
      }

      const { data } = await client.query({
        query: CART_PAGE_ITEM,
        fetchPolicy: "network-only",
        variables: {
          accessToken: authToken,
          userId: userId,
          campaignId: "",
          edit: edit,
          startDate: packageItem?.startDate || "",
          endDate: packageItem?.endDate || "",
          exposure: import.meta.env.VITE_APP_EXPOSURE
        }
      });

      if (data?.cartPageItem) {
        logger.log("Fetched cart details:", data);

        setCartDataList(data.cartPageItem);
        setCartCount(data.cartPageItem.length || 0);
      }
    } catch (error) {
      logger.error('Error fetching cart details:', error);
      toast({
        title: "Error",
        description: "Failed to fetch cart details",
        variant: "destructive"
      });
    } finally {
      cartRequestInProgress.current = false;
      setIsCartLoading(false);
    }
  };

  const [packageItem, setPackageItem] = useState<PackageBillboard | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPackageDetails = async () => {
    setIsLoading(true);
    setError(null);

    try {
      if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
        // Use mock data
        setPackageItem(mockPackageDetails[0]);
      } else {
        // Use GraphQL API with either auth token or public token
        const authToken = localStorage.getItem('authToken') || '';
        const { data } = await client.query({
          query: GET_PACKAGE_DETAILS,
          variables: {
            accessToken: authToken,
            packageId: packageId,
            page: 1,
            size: 10
          },
          fetchPolicy: 'network-only'
        });
        logger.log("Fetched package details:", data);
        if (!data || !data.packageDetails) {
          throw new Error('Package not found');
        }
        setPackageItem(data.packageDetails);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch package details');
      setPackageItem(undefined);
    } finally {
      setIsLoading(false);
    }
  };

  // Effect to fetch package details when component mounts or ID changes
  useEffect(() => {
    if (packageId) {
      fetchPackageDetails();
    }
  }, [packageId]);

  // Extract images from inventories for carousel
  const packageImages = [
    packageItem?.thumbnail,
    "https://images.unsplash.com/photo-1449824913935-59a10b8d2000?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=400",
  ].filter(Boolean);

  // Automatically advance the carousel every 4 seconds
  useEffect(() => {
    if (packageImages.length > 1) {
      const interval = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % packageImages.length);
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [packageImages.length]);

  // Add effect to reset image index when images change
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [packageImages]);

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % packageImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + packageImages.length) % packageImages.length);
  };
  const handleBackClick = () => {
    // Use browser history to go back, preserving all filters and state
    navigate(-1);
  };

  const addToCart = async (pkg: any) => {
    try {
      setLoadingCartItemIds((prev) => new Set([...prev, pkg.id]));

      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        // Use local cart context only
        const cartItem: CartItem = {
          id: pkg.id,
          name: pkg.name,
          countryId: pkg.country?.id || 'N/A',
          countryName: pkg.country?.name || 'N/A',
          location: pkg.country?.name || '',
          state: '',
          price: (pkg.totalPrice || 0) * 100, // Convert to cents for cart context
          impressions: pkg.estimation?.potentialViews || 0,
          uniqueVisitors: pkg.monthlySummary?.uniqueVisitors || 0,
          frequency: pkg.monthlySummary?.frequency || 0,
          size: (pkg.inventory?.total || '').toString(),
          type: 'Package',
          status: (pkg.numberOfPurchase < pkg.purchaseLimitation) ? 'available' : 'sold-out',
          image: pkg.thumbnail || `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`,
          spots: undefined,
          campaignDuration: pkg.campaignDuration || undefined,
          dailyHours: undefined,
          isDeal: false,
          daypartSchedules: undefined,
          packageStartDate: pkg.startDate,
          packageEndDate: pkg.endDate,
          requiresCampaignDuration: false,
          packageDetails: pkg,
          currencyCode: pkg.currency || currencyCodeFromState || currencyCode || 'N/A',
          specifications: {
            resolution: 'Various',
            duration: `${formatDate(pkg.startDate)} - ${formatDate(pkg.endDate)}`
          },
          priceFormat: "spot-based"
        };

        addToCartContext(cartItem);
        toast({
          title: 'Added to cart',
          description: `${pkg.name} added to cart successfully!`,
          variant: 'default',
        });
        return;
      }

      // Helper to deep remove __typename recursively
      const removeTypename = (obj: any): any => {
        if (Array.isArray(obj)) return obj.map(removeTypename);
        if (obj && typeof obj === 'object') {
          const newObj: any = {};
          for (const key in obj) {
            if (key !== '__typename') {
              newObj[key] = removeTypename(obj[key]);
            }
          }
          return newObj;
        }
        return obj;
      };

      // Build input for mutation as per sample, but remove __typename from all nested objects
      const input = [{
        accessToken: authToken,
        userId: localStorage.getItem('userId') || '',
        campaignName: pkg.name || '',
        startDate: pkg.validityStartDate || pkg.startDate || '',
        endDate: pkg.validityEndDate || pkg.endDate || '',
        cartItemName: pkg.name || '',
        cartItemId: pkg.id || '',
        cartItemReferenceId: null,
        cartItemType: 'PACKAGE',
        cartItemCountry: pkg.country?.name || '',
        cartItemVenueType: null,
        cartItemResolution: '',
        cartItemThumbnailUrl: pkg.thumbnail || '',
        cartItemLatitude: null,
        cartItemLongitude: null,
        packageDetails: {
          ...removeTypename({
            id: pkg.id || '',
            name: pkg.name || '',
            description: pkg.description || '',
            country: pkg.country ? {
              id: pkg.country.id || '',
              name: pkg.country.name || '',
              timezone: pkg.country.timezone || ''
            } : null,
            publisher: pkg.publisher ? {
              id: pkg.publisher.id || '',
              name: pkg.publisher.name || ''
            } : null,
            startDate: pkg.startDate || '',
            endDate: pkg.endDate || '',
            validityStartDate: pkg.validityStartDate || '',
            validityEndDate: pkg.validityEndDate || '',
            registryPackageType: pkg.registryPackageType || '',
            campaignDuration: pkg.campaignDuration || 0,
            purchaseLimitation: pkg.purchaseLimitation || 0,
            numberOfPurchase: pkg.numberOfPurchase || 0,
            inventory: pkg.inventory || {
              type: [],
              total: 0,
              classic: 0,
              digital: 0
            },
            exposure: pkg.exposure || [],
            currency: pkg.currency || currencyCodeFromState || currencyCode || '',
            price: pkg.price || 0,
            discount: pkg.discount || 0,
            totalPrice: pkg.totalPrice || 0,
            thumbnail: pkg.thumbnail || '',
            estimation: pkg.estimation || {
              potentialViews: 0,
              uniqueReach: 0,
              avgFrequency: 0,
              cpm: 0,
              spotsPerHour: 0,
              totalSpots: 0
            },
            inventories: pkg.inventories || [],
            publishTo: pkg.publishTo || [],
            tagIds: [],
            tags: pkg.tags || [],
            reopened: pkg.reopened || false,
            createdBy: pkg.createdBy || '',
            createdDate: pkg.createdDate || '',
            lastModifiedBy: pkg.lastModifiedBy || '',
            lastModifiedDate: pkg.lastModifiedDate || '',
            type: 'PACKAGE'
          })
        },
        discount: pkg.discount ? (pkg.price * pkg.discount) / 100 : 0,
        nowPayPrice: pkg.totalPrice || 0,
        totalPrice: pkg.price || 0,
        spotDuration: null
      }];

      const { data } = await addCampaignCart({ variables: { input } });
      const itemDataArr = data?.addCampaignCart?.itemData;
      const itemData = Array.isArray(itemDataArr) ? itemDataArr[0] : undefined;
      if (itemData && itemData.success) {
        toast({
          title: 'Added to cart',
          description: `${pkg.name} added to cart successfully!`,
          variant: 'default',
        });
        // Update local cart context for UI
        addToCartContext({
          id: pkg.id,
          name: pkg.name,
          // countryId: pkg.countryId,
          // countryName: pkg.countryName,
          countryId: pkg.country?.id || 'N/A',
          countryName: pkg.country?.name || 'N/A',
          location: pkg.country?.name || '',
          state: '',
          price: pkg.totalPrice || 0,
          impressions: pkg.estimation?.potentialViews || 0,
          uniqueVisitors: pkg.monthlySummary?.uniqueVisitors || 0,
          frequency: pkg.monthlySummary?.frequency || 0,
          size: (pkg.inventory?.total || '').toString(),
          type: 'Package',
          status: (pkg.numberOfPurchase < pkg.purchaseLimitation) ? 'available' : 'sold-out',
          image: pkg.thumbnail || '',
          spots: undefined,
          campaignDuration: pkg.campaignDuration || undefined,
          dailyHours: undefined,
          isDeal: false,
          daypartSchedules: undefined,
          packageStartDate: pkg.startDate,
          packageEndDate: pkg.endDate,
          requiresCampaignDuration: false,
          currencyCode: pkg.currency || currencyCodeFromState || currencyCode || 'N/A',
          specifications: {
            resolution: 'Various',
            duration: `${formatDate(pkg.startDate)} - ${formatDate(pkg.endDate)}`
          },
          priceFormat: "spot-based"
        });
      } else {
        const errorMessage = (itemData && itemData.message) || 'Failed to add to cart.';
        toast({
          title: 'Error',
          description: errorMessage,
          variant: 'destructive',
        });
      }
    } catch (err: any) {
      logger.error('Error adding to cart:', err);
      toast({
        title: 'Error',
        description: err.message || 'An unexpected error occurred while adding to cart.',
        variant: 'destructive',
      });
    } finally {
      setLoadingCartItemIds((prev) => {
        const next = new Set(prev);
        next.delete(pkg.id);
        return next;
      });
    }
  };

  const safeFormatDate = (date: Date | string | null, formatStr: string, fallback: string = ''): string => {
    if (!date) return fallback;
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      return format(dateObj, formatStr);
    } catch (error) {
      logger.error("Error formatting date:", error);
      return fallback;
    }
  };

  const deleteCartItem = async (pkg: any) => {
    const trackingId = pkg.id;

    // For non-authenticated users, use context only
    if (!localStorage.getItem('authToken')) {
      try {
        setLoadingCartItemIds(prev => new Set([...prev, trackingId]));
        removeFromCartContext(pkg.id);
        toast({
          title: 'Removed from cart',
          description: `${pkg.name} removed from cart successfully!`,
          variant: 'default',
        });
      } finally {
        setLoadingCartItemIds(prev => {
          const next = new Set(prev);
          next.delete(trackingId);
          return next;
        });
      }
      return;
    }

    // Double check for valid item data
    if (!pkg) {
      logger.error('Invalid package data:', pkg);
      toast({
        title: "Error",
        description: "Invalid package data",
        variant: "destructive"
      });
      return;
    }

    // Check if item is already being processed
    if (loadingCartItemIds.has(trackingId)) {
      return;
    }
    // Find cartItemId from cartDataList
    const matchingItem = cartDataList.find((list: any) => {
      return list.campaignInventories?.some((item: any) => item.billboardId === pkg.id);
    });

    if (!matchingItem) {
      logger.error('Item not found in cart:', pkg.id);
      toast({
        title: "Error",
        description: "Item not found in cart",
        variant: "destructive"
      });
      return;
    }

    const matchedInventory = matchingItem.campaignInventories?.find((item: any) => item.billboardId === pkg.id);
    const cartItemId = matchedInventory?.cartItemId;

    // Save previous states for rollback if needed
    const previousCartCount = cartCount;

    try {
      // Add to loading set using the tracking ID
      setLoadingCartItemIds(prev => new Set([...prev, trackingId]));

      // Optimistically update UI states
      setCartCount(prev => Math.max(0, prev - 1));

      // Call deletion mutation with cartItemId from the cart data
      const { data, errors } = await deleteCartQuery({
        variables: {
          accessToken: localStorage.getItem('authToken'),
          deleteItems: [{
            id: cartItemId, // Use cartItemId from cartDataList
            itemType: 'cart-data'
          }]
        },
      });

      // Handle successful deletion
      if (data?.deleteCartItem?.success) {
        // Remove from cart context
        removeFromCartContext(pkg.id.toString());

        // Refresh cart data
        await fetchCartDetails();

        toast({
          title: "Success",
          description: data.deleteCartItem.message || "Package removed from cart successfully",
          variant: "default"
        });

        // Notify other components
        window.dispatchEvent(new Event('cartUpdated'));
      } else {
        // Handle failure
        const errorMessage = errors?.[0]?.message || data?.deleteCartItem?.message || "Failed to delete package";
        logger.error('Delete failed:', errorMessage);
        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive"
        });

        // Revert state changes
        setCartCount(previousCartCount);
      }
    } catch (error: any) {
      logger.error("Error in deleteCartItem:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to remove package from cart",
        variant: "destructive"
      });

      // Revert state changes
      setCartCount(previousCartCount);
    } finally {
      // Clean up loading state using the tracking ID
      setLoadingCartItemIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(trackingId);
        return newSet;
      });
    }
  };

  const handleViewDetails = (inventoryItem: any) => {
    logger.log("Viewing details for inventory item:", inventoryItem);

    const billboardType = inventoryItem.inventoryType || inventoryItem.type || 'digital';
    const startDate = packageItem?.startDate || '';
    const endDate = packageItem?.endDate || '';
    const campaignName = packageItem?.name || '';
    if (isLoggedIn) {
      navigate(`/property-details/${inventoryItem.inventoryId}?type=${encodeURIComponent(billboardType)}`, {
        state: {
          currentPrice: inventoryItem.sellingRate || inventoryItem.price,
          startDate,
          endDate,
          campaignName,
          from: 'package',
          packageId: packageItem?.id || '',
          currencyCode: packageItem?.currency || currencyCodeFromState || 'N/A'
        }
      });
    } else {
      navigate(`${base_path}/billboard/${inventoryItem.inventoryId}?type=${encodeURIComponent(billboardType)}`, {
        state: {
          currentPrice: inventoryItem.sellingRate || inventoryItem.price,
          from: 'package',
          packageId: packageItem?.id || '',
          currencyCode: packageItem?.currency || currencyCodeFromState || 'N/A'
        }
      });
    }

    // if (filters.category === 'deals' || inventoryItem.discountedPrice || inventoryItem.originalPrice) {
    //   navigate(`/deal/${inventoryItem.id}`);
    // } else if (inventoryItem.inventoryType === "Package") {
    //   navigate(`/package/${inventoryItem.id}`);
    // } else {
    //   navigate(`/property-details/${inventoryItem.id}?type=${encodeURIComponent(billboardType)}`, {
    //     state: {
    //       currentPrice: inventoryItem.currentPrice,
    //       startDate,
    //       endDate,
    //       campaignName
    //     }
    //   });
    // }
  };

  // Helper function to format date
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Helper function to check if package is available based on campaign dates
  const isPackageAvailable = (pkg: any): boolean => {
    const validityStart = pkg.startDate ? new Date(pkg.startDate) : null;
    const validityEnd = pkg.endDate ? new Date(pkg.endDate) : null;

    if (!validityStart || !validityEnd) return true; // If no validity dates, assume available

    const currentDate = new Date();

    // Package is available if current date is within validity period
    return currentDate >= validityStart && currentDate <= validityEnd;
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Loading package details...</h2>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error loading package details</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button onClick={handleBackClick}>Back to {isLoggedIn ? 'Campaigns' : 'Explore'}</Button>
        </div>
      </div>
    );
  }

  // Show not found state
  if (!packageItem) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Package not found</h2>
          <Button onClick={handleBackClick}>Back to {isLoggedIn ? 'Campaigns' : 'Explore'}</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-[95vw] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header with back button and title */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{packageItem.name}</h1>
            <div className="flex items-center space-x-4">
              {/* <Badge className={packageItem.numberOfPurchase < packageItem.purchaseLimitation ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}>
                {packageItem.numberOfPurchase < packageItem.purchaseLimitation ? 'Available' : 'Sold Out'}
              </Badge> */}
              <Badge className="bg-emerald-500 text-white">
                {packageItem.inventory.total} {packageItem.inventory.total === 1 ? 'Billboard' : 'Billboards'}
              </Badge>
              {isLoggedIn && (
                <div className="flex items-center text-gray-600">
                  <Eye className="mr-1 h-4 w-4" />
                  <span>{packageItem?.estimation?.potentialViews?.toLocaleString() || 'N/A'} Impressions</span>
                </div>
              )}
            </div>
          </div>
          <Button
            variant="outline"
            onClick={() => handleBackClick()}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8 lg:items-start">
          {/* Image Carousel and Package Details */}
          <div className="flex flex-col space-y-6 h-full">
            {/* Image Carousel */}
            <Card className="overflow-hidden">
              <div className="relative h-96">
                {packageImages.length > 0 && (
                  <>
                    <img
                      src={packageImages[currentImageIndex] || `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`}
                      className="w-full h-full object-cover transition-opacity duration-500"
                      alt={`${packageItem.name} - Image ${currentImageIndex + 1}`}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`;
                      }}
                    />

                    {packageImages.length > 1 && (
                      <>
                        {/* <Button
                          variant="outline"
                          size="sm"
                          className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white"
                          onClick={prevImage}
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white"
                          onClick={nextImage}
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button> */}
                        {/* <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                          {packageImages.map((_, index) => (
                            <button
                              key={index}
                              className={`w-2 h-2 rounded-full ${index === currentImageIndex ? 'bg-white' : 'bg-white/50'
                                }`}
                              onClick={() => setCurrentImageIndex(index)}
                            />
                          ))}
                        </div> */}
                      </>
                    )}
                  </>
                )}
              </div>
            </Card>

            {/* Package Details Section */}
            <Card className="flex-1">
              <CardContent className="p-6 flex flex-col h-full">
                <h3 className="text-lg font-semibold mb-4">Package Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 flex-1">
                  <div>
                    <span className="text-sm text-gray-600">Package Type:</span>
                    <p className="font-medium text-gray-900">{packageItem.registryPackageType || 'Bundle'}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">Package Duration:</span>
                    {/* <p className="font-medium text-gray-900">{packageItem.campaignDuration ? `${packageItem.campaignDuration} days` : '24 months'}</p> */}
                    <p className="font-medium">{packageItem.validityStartDate && packageItem.validityEndDate ? `${Math.ceil((new Date(packageItem.validityEndDate).getTime() - new Date(packageItem.validityStartDate).getTime()) / (1000 * 60 * 60 * 24)) + 1} days` : 'Duration not specified'}</p>
                  </div>

                  <div>
                    <span className="text-sm text-gray-600">Package Description:</span>
                    <p className="font-medium text-gray-900">{packageItem.description || 'Premium package with multiple high-visibility locations across major metropolitan areas.'}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">Package Period:</span>
                    <p className="font-medium text-gray-900">
                      {packageItem.validityStartDate && packageItem.validityEndDate
                        ? `${safeFormatDate(new Date(packageItem.validityStartDate), 'dd MMM yyyy', '-')} to ${safeFormatDate(new Date(packageItem.validityEndDate), 'dd MMM yyyy', '-')}`
                        : 'Period not specified'
                      }
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>


          {/* Package Summary */}
          <div className="flex-shrink-0 lg:flex-[2]">
            <Card className="h-full">
              <CardContent className="p-6 flex flex-col h-full">
                <h3 className="text-lg font-semibold mb-4">Package Summary</h3>
                <div className="space-y-4 flex-1">
                  {/* Media Owner Section */}
                  {/* {packageItem.publisher && (() => {
                    return (
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm text-gray-600">Media Owner:</span>
                        <div className="flex items-center justify-between mt-2">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                              {packageItem.publisher.name.charAt(0).toUpperCase()}
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{packageItem.publisher.name}</p>
                              <p className="text-xs text-gray-500 truncate">{packageItem.country.name}</p>
                            </div>
                          </div>
                          <Link to={`/store/${packageItem.publisher.id}`}>
                            <Button size="sm" variant="outline" className="text-xs px-2 py-1 h-7">
                              Visit Store
                            </Button>
                          </Link>
                        </div>
                      </div>
                    );
                  })()} */}

                  <div>
                    <span className="text-sm text-gray-600">Package Name:</span>
                    <p className="font-medium">{packageItem.name}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">Package Validity:</span>
                    <p className="font-medium">{safeFormatDate(packageItem.startDate ? new Date(packageItem.startDate) : null, 'dd MMM yyyy', '-')} - {safeFormatDate(packageItem.endDate ? new Date(packageItem.endDate) : null, 'dd MMM yyyy', '-')}</p>
                    {/* <p className="font-medium">{packageItem.validityStartDate && packageItem.validityEndDate ? `${formatDate(packageItem.validityStartDate)} - ${formatDate(packageItem.validityEndDate)}` : 'Duration not specified'}</p> */}
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">Locations:</span>
                    <p className="font-medium">{packageItem?.inventories?.length || 0} billboards</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">Minimum Duration:</span>
                    <p className="font-medium">{packageItem.campaignDuration || 'N/A'} days</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">Discount:</span>
                    <p className="font-medium text-emerald-600">{packageItem.discount || 0}% off regular price</p>
                  </div>
                  {/* <div>
                    <span className="text-sm text-gray-600">Availability:</span>
                    <p className="font-medium">
                      <Badge className={packageItem.numberOfPurchase < packageItem.purchaseLimitation ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}>
                        {packageItem.numberOfPurchase < packageItem.purchaseLimitation ? '✓ Available' : 'Unavailable'}
                      </Badge>
                    </p>
                  </div> */}
                </div>

                {/* Fixed bottom section */}
                <div className="mt-auto">
                  <div className="pt-4 border-t space-y-2">
                    {packageItem.discount > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Standard Price:</span>
                        <span className="text-lg text-gray-500 line-through">{packageItem.currency || currencyCodeFromState} {packageItem.price?.toLocaleString() || 'N/A'}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-semibold">Our Offer:</span>
                      <span className="text-2xl font-bold text-emerald-600">{packageItem.currency || currencyCodeFromState} {(packageItem.totalPrice || packageItem.price)?.toLocaleString() || 'N/A'}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Price includes all selected billboard locations</p>
                  </div>
                  <div className="space-y-2 mt-4">
                    <Button
                      onClick={isInCart(packageItem.id) ? () => deleteCartItem(packageItem) : () => addToCart(packageItem)}
                      disabled={(isLoggedIn && (packageItem.numberOfPurchase >= packageItem.purchaseLimitation || !isPackageAvailable(packageItem))) || loadingCartItemIds.has(packageItem.id)}
                      className={`w-full ${isInCart(packageItem.id)
                        ? "bg-red-600 hover:bg-red-700 text-white"
                        : "bg-emerald-600 hover:bg-emerald-700 text-white"
                        }`}
                    >
                      <ShoppingCart className="mr-2 h-4 w-4" />
                      {(isLoggedIn && packageItem.numberOfPurchase >= packageItem.purchaseLimitation)
                        ? 'Sold Out'
                        : (isLoggedIn && !isPackageAvailable(packageItem))
                          ? 'UnAvailable'
                          : loadingCartItemIds.has(packageItem.id)
                            ? 'Processing...'
                            : isInCart(packageItem.id)
                              ? 'Remove from Cart'
                              : 'Add to Cart'
                      }
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Package Overview Section */}
        {isLoggedIn ? (
          <Card className="mt-8">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold flex items-center">
                  <Monitor className="mr-2 h-5 w-5 text-emerald-600" />
                  Package Overview
                </h3>
              </div>

              <div className="grid grid-cols-4 gap-6">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-center mb-2">
                    <Eye className="h-6 w-6 text-emerald-600" />
                  </div>
                  <div className="text-sm text-gray-600 mb-1">IMPRESSIONS</div>
                  <div className="text-xl font-bold text-gray-900">
                    {packageItem.estimation?.potentialViews?.toLocaleString() || 'N/A'}
                  </div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-center mb-2">
                    <MapPin className="h-6 w-6 text-green-600" />
                  </div>
                  <div className="text-sm text-gray-600 mb-1">UNIQUE REACH</div>
                  <div className="text-xl font-bold text-gray-900">
                    {packageItem.estimation?.uniqueReach?.toLocaleString() || 'N/A'}
                  </div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-center mb-2">
                    <Zap className="h-6 w-6 text-yellow-600" />
                  </div>
                  <div className="text-sm text-gray-600 mb-1">AVG FREQUENCY</div>
                  <div className="text-xl font-bold text-gray-900">
                    {packageItem.estimation?.avgFrequency?.toFixed(2) || 'N/A'}
                  </div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-center mb-2">
                    <Monitor className="h-6 w-6 text-purple-600" />
                  </div>
                  <div className="text-sm text-gray-600 mb-1">TOTAL SCREENS</div>
                  <div className="text-xl font-bold text-gray-900">
                    {packageItem.inventory?.total || 'N/A'}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="mt-8">
            <CardContent className="p-6">
              <div className="flex flex-col items-center justify-center py-12">
                <Lock className="h-16 w-16 text-gray-400 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Login to view full audience and performance data</h3>
                <p className="text-sm text-gray-600 mb-6 text-center max-w-md">
                  Sign in to access detailed package overview including impressions, unique reach, and frequency data.
                </p>
                <Button
                  onClick={() => navigate('/login')}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  Login to View Details
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Billboard Locations */}
        <Card className="mt-8">
          <CardContent className="p-6">
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Inventory Locations
              </h3>
              <p className="text-sm text-gray-600">
                {packageItem?.inventories?.length || 0} locations
              </p>
            </div>

            <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
              <table className="w-full border-collapse table-fixed">
                <thead className="sticky top-0 bg-white z-10">
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-medium text-gray-900 w-1/3">Billboard Name</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900 w-1/6">Type</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900 w-1/6">Size</th>
                    {isLoggedIn && <th className="text-left py-3 px-4 font-medium text-gray-900 w-1/6">Impressions</th>}
                    <th className="text-left py-3 px-4 font-medium text-gray-900 w-1/6">Address</th>
                  </tr>
                </thead>
                {packageItem.inventories && packageItem.inventories.length > 0 ? (
                  <tbody>
                    {packageItem.inventories.map((billboard: Inventory, index: number) => (
                      <tr
                        key={billboard.inventoryId}
                        className={`${index !== packageItem.inventories.length - 1 ? 'border-b border-gray-300' : ''}`}
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center space-x-3">
                            <img
                              src={billboard.thumbnail}
                              alt={billboard.name}
                              className="w-8 h-8 rounded object-cover"
                              onError={(e) => {
                                (e.target as HTMLImageElement).src = `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`;
                              }}
                            />
                            <span className="text-sm font-medium text-gray-900">{billboard.name}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">{billboard.type}</td>
                        <td className="py-3 px-4 text-sm text-gray-600">{billboard.size || 'N/A'}</td>
                        {isLoggedIn && (
                          <td className="py-3 px-4 text-sm text-gray-600">
                            {billboard?.estimation?.potentialViews?.toLocaleString() || 'N/A'}
                          </td>
                        )}
                        <td className="py-3 px-4 text-sm text-gray-600">{billboard.location}</td>
                      </tr>
                    ))}
                  </tbody>
                ) : (
                  <tbody>
                    <tr>
                      <td colSpan={isLoggedIn ? 5 : 4} className="py-3 px-4 text-sm text-gray-600 text-center">
                        No locations found
                      </td>
                    </tr>
                  </tbody>
                )}
              </table>
            </div>

          </CardContent>
        </Card>
      </div>
    </div>
  );
}
